// Styles of the form renderer (spec §8). A site passes `styles`:
//   styles.default          every field        styles.<type>      one field type (`text`, `select`, `custom:stars`...)
//   styles.group            groups             styles.form        the form itself
// each holding slots (`root`, `label`, `input`, `hint`, `error` for a field), and a slot is a className string, a
// style object, or { className, style }. Without `styles` the default CSS module (class_names) gives the look.

const is_object = (value) => value !== null && typeof value === "object" && !Array.isArray(value);

// one slot value -> { className, style }
export const normalize_slot = (value) => {
    if (typeof value === "string") {
        return { className: value, style: undefined };
    }
    if (is_object(value) && (typeof value.className === "string" || is_object(value.style))) {
        return { className: value.className, style: is_object(value.style) ? value.style : undefined };
    }
    if (is_object(value)) {
        return { className: undefined, style: value };
    }
    return { className: undefined, style: undefined };
};

// The names a site can write CSS for (they never change, unlike the default module's): a field has `fb-field` and
// `fb-field-<type>`, its parts `fb-label`, `fb-input`, `fb-hint`, `fb-error`, `fb-control`, `fb-adornment` (each also with
// `-<type>`); a group `fb-group`, `fb-group-title`...; the form `fb-form`, `fb-form-submit`... A custom type "custom:card-number" is `card-number`.
const SLOT_ALIASES = { root: "field" };
const type_slug = (type) => String(type).replace(/^custom:/, "").replace(/[^A-Za-z0-9_-]/g, "-");
// Only when the hook was given `stable_class_names` (it marks the class_names it hands down with STABLE_FLAG): a form that did not ask draws the markup it always did.
export const STABLE_FLAG = "__stable";
export const stable_names = ({ slot, type, scope, class_names }) => {
    if (class_names?.[STABLE_FLAG] !== true) {
        return undefined;
    }
    if (scope) {
        return slot === "root" ? `fb-${scope}` : `fb-${scope}-${slot}`;
    }
    const alias = SLOT_ALIASES[slot] || slot;
    return type ? `fb-${alias} fb-${alias}-${type_slug(type)}` : `fb-${alias}`;
};

export const join_classes = (...names) => names.filter((name) => typeof name === "string" && name.trim() !== "").join(" ");

/*
 * What an element of `slot` gets: the default module's class, then the site's `styles.default`, then the one for the
 * field's own type. `extra` adds state classes (invalid, disabled...). Returns props to spread: { className, style }.
 */
export const slot_props = ({ slot, class_names, styles, type, extra }) => {
    const sources = [styles?.default?.[slot], type ? styles?.[type]?.[slot] : undefined].map(normalize_slot);
    const style = Object.assign({}, ...sources.map((item) => item.style).filter(Boolean));
    const className = join_classes(stable_names({ slot, type, class_names }), class_names?.[slot], extra, ...sources.map((item) => item.className));
    const result = {};
    if (className !== "") {
        result.className = className;
    }
    if (Object.keys(style).length > 0) {
        result.style = style;
    }
    return result;
};

// group and form slots live under their own name: styles.group.title, styles.form.submit
export const scope_props = ({ scope, slot, class_names, styles, extra }) => {
    const own = normalize_slot(styles?.[scope]?.[slot]);
    const className = join_classes(stable_names({ slot, scope, class_names }), class_names?.[`${scope}_${slot}`], extra, own.className);
    const result = {};
    if (className !== "") {
        result.className = className;
    }
    if (own.style) {
        result.style = own.style;
    }
    return result;
};
